import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * The same file, and deliberately the same imagetools query, as the home
 * hero's Toyota slide (`config/heroVehicles.ts`). Vite keys a transformed
 * asset by its full specifier, so matching the query to the character means
 * the two importers share one set of renditions instead of doubling them; a
 * stray `w=` here would ship the same car twice.
 *
 * It is the slider cut-out and not `assets/photos/toyota_land-cruiser.jpg`
 * because that file is catalogue artwork on a pure white background — over
 * half its pixels are #FFF — so there is nothing to lay over a dark band. The
 * cut-out is the same car with the background already removed.
 */
import landCruiser from '@/assets/slider/toyota-land-cruiser.webp?trim&w=720;1040&fit=inside&format=avif;webp&as=picture';
import { AppButton, type SectionBackdropProps, WhatsAppButton } from '@/components/atoms';
import { quoteCta } from '@/config/navigation';

import { Section } from './Section';

/**
 * What every quotation band wears unless its page says otherwise. This used
 * to be opt-in, on the grounds that a silhouette baked in here would be a
 * site-wide change — which it is, and which is now the point: the band closes
 * eight pages and should close all eight the same way.
 *
 * `feature` rather than the watermark because this band is the one thing on
 * the page that has to be looked at. The contrast that buys is paid for by
 * the scrim in `theme/backdrops.ts`, not by the copy.
 */
const defaultBackdrop: SectionBackdropProps = {
  picture: landCruiser,
  emphasis: 'feature',
};

type Props = {
  title?: string;
  text?: string;
  /** Prefilled WhatsApp message for this page. */
  whatsappMessage?: string;
  /**
   * Overrides the Land Cruiser for a page that wants its own car. Pass the
   * whole object, not a partial: a page naming a backdrop is choosing the
   * arrangement as well as the picture, so it also decides the emphasis and
   * the corner.
   */
  backdrop?: SectionBackdropProps;
};

/** The global quotation call to action (§27), closing most pages. */
export function QuoteBand({
  title = 'Get my car quotation',
  text = 'Tell us the car you want and your budget. Our team will send you a quotation and guide you through the next steps.',
  whatsappMessage = 'Hello Kabex Motors, I would like a quotation for a car.',
  backdrop = defaultBackdrop,
}: Props) {
  return (
    <Section tone="inverse" labelledBy="quote-band-title" backdrop={backdrop}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={8}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack spacing={3} sx={{ maxWidth: 640 }}>
          <Typography id="quote-band-title" variant="h3" component="h2">
            {title}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.78)' }}>{text}</Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
        >
          <AppButton to={quoteCta.to} size="large">
            Get My Car Quotation
          </AppButton>
          <WhatsAppButton message={whatsappMessage} size="large" />
        </Stack>
      </Stack>
    </Section>
  );
}
